// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the canvas renderer + juice. The web is drawn in a centred square so beads never
// distort, on a dark board: clean threads glow thin cyan, crossed threads glow THICK coral
// with a pulsing ✕ where they cut (a shape cue on top of the colour — colour-blind safe), and
// beads are amber discs. A grabbed bead lifts and brightens; dropping it clears the crossings
// it removed with a spark; solving the web flashes it green. Particles + shake come from the
// game's event stream. No gameplay logic lives here — it reads GameState (and a live drag
// preview) and paints; crossing colouring is recomputed from the drawn positions each frame.

import { crossedEdges, crossingMarks, type Edge, type Pt } from './geom';
import type { GameEvent, GameState } from './game';
import { PALETTE } from './palette';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
  r: number;
}

export interface RenderOpts {
  dtMs: number;
  reducedMotion: boolean;
}

const MAX_PARTICLES = 180;

export class Board {
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private cw = 0;
  private ch = 0;
  private side = 10;
  private ox = 0;
  private oy = 0;

  private nodes: Pt[] = [];
  private edges: Edge[] = [];
  private particles: Particle[] = [];
  private shake = 0;
  private pulse = 0;
  private solveFlash = 0;
  private redFlash = 0;
  private lastMoved = -1;

  private dragIndex: number | null = null;
  private dragPos: Pt | null = null;
  private grabLift = 0;
  private selected: number | null = null;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  setGraph(nodes: Pt[], edges: Edge[]): void {
    this.nodes = nodes;
    this.edges = edges;
    this.particles = [];
    this.shake = 0;
    this.dragIndex = null;
    this.dragPos = null;
    this.selected = null;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return; // ignore a transient 0-size measure
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.cw = rect.width;
    this.ch = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.layout();
  }

  private layout(): void {
    const pad = 20;
    this.side = Math.max(10, Math.min(this.cw, this.ch) - pad * 2);
    this.ox = (this.cw - this.side) / 2;
    this.oy = (this.ch - this.side) / 2;
  }

  private wx(nx: number): number {
    return this.ox + nx * this.side;
  }
  private wy(ny: number): number {
    return this.oy + ny * this.side;
  }
  private beadR(): number {
    return Math.max(9, Math.min(20, this.side * 0.036));
  }

  /** Effective (possibly drag-previewed) position of a node. */
  private eff(i: number): Pt {
    return this.dragIndex === i && this.dragPos ? this.dragPos : this.nodes[i];
  }

  // ── input support ──────────────────────────────────────────────────────────
  nodeAt(cssX: number, cssY: number): number | null {
    const hit = Math.max(26, this.beadR() * 1.9);
    let best = -1;
    let bestD = hit;
    for (let i = 0; i < this.nodes.length; i++) {
      const p = this.eff(i);
      const d = Math.hypot(cssX - this.wx(p.x), cssY - this.wy(p.y));
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best < 0 ? null : best;
  }
  toNorm(cssX: number, cssY: number): { x: number; y: number } {
    return { x: (cssX - this.ox) / this.side, y: (cssY - this.oy) / this.side };
  }
  nodeCenterCss(i: number): { x: number; y: number } | null {
    const p = this.nodes[i];
    return p ? { x: this.wx(p.x), y: this.wy(p.y) } : null;
  }
  nodeNorm(i: number): { x: number; y: number } | null {
    const p = this.nodes[i];
    return p ? { x: p.x, y: p.y } : null;
  }
  centersCss(): Array<{ x: number; y: number }> {
    return this.nodes.map((p) => ({ x: this.wx(p.x), y: this.wy(p.y) }));
  }

  beginDrag(i: number): void {
    this.dragIndex = i;
    this.dragPos = { ...this.nodes[i] };
    this.grabLift = 0;
  }
  dragTo(nx: number, ny: number): void {
    if (this.dragIndex === null) return;
    this.dragPos = { x: nx, y: ny };
  }
  endDrag(): void {
    this.dragIndex = null;
    this.dragPos = null;
  }
  setSelected(i: number | null): void {
    this.selected = i;
  }

  // ── juice ────────────────────────────────────────────────────────────────
  onEvents(events: GameEvent[], reduced: boolean): void {
    for (const e of events) {
      if (e.t === 'move') this.lastMoved = e.i;
      else if (e.t === 'clear') {
        const p = this.lastMoved >= 0 ? this.nodes[this.lastMoved] : { x: 0.5, y: 0.5 };
        this.burst(p, PALETTE.mark, reduced ? 4 : Math.min(18, 4 + e.count * 4), 3.2);
      } else if (e.t === 'solve') {
        this.solveFlash = 1;
        // a burst from a handful of beads, not every one — a big web must not spawn a cloud
        const step = Math.max(1, Math.ceil(this.nodes.length / 6));
        for (let i = 0; i < this.nodes.length; i += step) this.burst(this.nodes[i], PALETTE.solved, reduced ? 3 : 7, 3.4);
        if (!reduced) this.shake = Math.max(this.shake, 4);
      } else if (e.t === 'next') {
        this.dragIndex = null;
        this.dragPos = null;
        this.selected = null;
        this.lastMoved = -1;
      } else if (e.t === 'budget') {
        this.redFlash = 1;
        if (!reduced) this.shake = Math.max(this.shake, 9);
      }
    }
  }

  private burst(at: Pt, col: string, n: number, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.7;
      const s = speed * (0.4 + Math.random() * 0.9) * 0.01; // normalized-space velocity
      this.particles.push({ x: at.x, y: at.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 0, max: 0.4 + Math.random() * 0.5, col, r: 1.4 + Math.random() * 2.2 });
    }
    // hard cap: a backgrounded tab (or a very fast solver) must never grow the pool unbounded
    if (this.particles.length > MAX_PARTICLES) this.particles.splice(0, this.particles.length - MAX_PARTICLES);
  }

  frame(s: GameState, opts: RenderOpts): void {
    const ctx = this.ctx;
    const dt = Math.min(opts.dtMs, 60) / 1000;
    this.pulse += dt;
    this.nodes = s.nodes;
    this.edges = s.edges;
    if (this.side < 12) this.layout();

    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 26);
    if (this.solveFlash > 0) this.solveFlash = Math.max(0, this.solveFlash - dt * 1.4);
    if (this.redFlash > 0) this.redFlash = Math.max(0, this.redFlash - dt * 1.6);
    this.grabLift = Math.min(1, this.grabLift + dt * 8);
    for (const p of this.particles) {
      p.life += dt;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.9;
      p.vy *= 0.9;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);

    // effective positions for colouring + drawing
    const pts = this.nodes.map((_, i) => this.eff(i));
    const crossed = crossedEdges(pts, this.edges);
    const marks = crossingMarks(pts, this.edges);
    const solvedNow = marks.length === 0;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.cw, this.ch);
    if (this.shake > 0.2) {
      const a = this.pulse * 60;
      ctx.translate(Math.sin(a * 1.7) * this.shake * 0.6, Math.cos(a * 2.3) * this.shake * 0.6);
    }

    if (this.redFlash > 0) {
      ctx.fillStyle = `rgba(255,90,80,${0.18 * this.redFlash})`;
      ctx.fillRect(-40, -40, this.cw + 80, this.ch + 80);
    }

    this.drawThreads(ctx, pts, crossed, solvedNow);
    this.drawMarks(ctx, marks);
    this.drawBeads(ctx, pts, solvedNow);
    this.drawParticles(ctx);
    ctx.restore();
  }

  private drawThreads(ctx: CanvasRenderingContext2D, pts: Pt[], crossed: boolean[], solved: boolean): void {
    const clean = solved ? PALETTE.solved : PALETTE.clean;
    ctx.lineCap = 'round';
    // clean first, crossed on top so a crossing is never hidden under a clean line
    for (let pass = 0; pass < 2; pass++) {
      for (let k = 0; k < this.edges.length; k++) {
        const isCross = crossed[k] && !solved;
        if ((pass === 0) === isCross) continue;
        const [a, b] = this.edges[k];
        ctx.strokeStyle = isCross ? PALETTE.crossed : clean;
        ctx.lineWidth = isCross ? Math.max(3.5, this.side * 0.011) : Math.max(2, this.side * 0.0065);
        ctx.globalAlpha = isCross ? 0.96 : 0.82;
        ctx.beginPath();
        ctx.moveTo(this.wx(pts[a].x), this.wy(pts[a].y));
        ctx.lineTo(this.wx(pts[b].x), this.wy(pts[b].y));
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  private drawMarks(ctx: CanvasRenderingContext2D, marks: Pt[]): void {
    const r = Math.max(4, this.side * 0.014);
    const grow = 1 + Math.sin(this.pulse * 6) * 0.14;
    ctx.strokeStyle = PALETTE.mark;
    ctx.lineWidth = Math.max(2, this.side * 0.006);
    ctx.lineCap = 'round';
    for (const m of marks) {
      const cx = this.wx(m.x);
      const cy = this.wy(m.y);
      const d = r * grow;
      ctx.beginPath();
      ctx.moveTo(cx - d, cy - d);
      ctx.lineTo(cx + d, cy + d);
      ctx.moveTo(cx + d, cy - d);
      ctx.lineTo(cx - d, cy + d);
      ctx.stroke();
    }
  }

  private drawBeads(ctx: CanvasRenderingContext2D, pts: Pt[], solved: boolean): void {
    const baseR = this.beadR();
    for (let i = 0; i < pts.length; i++) {
      const cx = this.wx(pts[i].x);
      const cy = this.wy(pts[i].y);
      const grabbed = this.dragIndex === i;
      const r = grabbed ? baseR * (1.18 + this.grabLift * 0.06) : baseR;

      // selection ring (keyboard)
      if (this.selected === i && !grabbed) {
        ctx.strokeStyle = PALETTE.beadGrab;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.arc(cx, cy, r + 5, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      // halo
      const halo = ctx.createRadialGradient(cx, cy, r * 0.4, cx, cy, r * (grabbed ? 2.4 : 1.7));
      halo.addColorStop(0, grabbed ? 'rgba(255,240,194,0.4)' : 'rgba(255,194,74,0.28)');
      halo.addColorStop(1, 'rgba(255,194,74,0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, cy, r * (grabbed ? 2.4 : 1.7), 0, Math.PI * 2);
      ctx.fill();
      // body
      const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.2, cx, cy, r);
      grad.addColorStop(0, grabbed ? '#fffbe9' : PALETTE.beadGrab);
      grad.addColorStop(1, solved ? PALETTE.solved : PALETTE.bead);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(32,22,3,0.55)';
      ctx.lineWidth = Math.max(1, r * 0.12);
      ctx.stroke();
    }
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      const a = 1 - p.life / p.max;
      ctx.globalAlpha = Math.max(0, a);
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(this.wx(p.x), this.wy(p.y), p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // ── contrast probes (device pixels) ────────────────────────────────────────
  /** Device-pixel centre of bead i. */
  nodePixel(i: number): { x: number; y: number } | null {
    const p = this.nodes[i];
    return p ? { x: this.wx(p.x) * this.dpr, y: this.wy(p.y) * this.dpr } : null;
  }
  /** Device-pixel midpoint of edge k. */
  edgePixel(k: number): { x: number; y: number } | null {
    const e = this.edges[k];
    if (!e) return null;
    const a = this.nodes[e[0]];
    const b = this.nodes[e[1]];
    return { x: this.wx((a.x + b.x) / 2) * this.dpr, y: this.wy((a.y + b.y) / 2) * this.dpr };
  }
  /** A device-pixel point on the empty board (top-left of the play box). */
  emptyPixel(): { x: number; y: number } {
    return { x: this.wx(0.02) * this.dpr, y: this.wy(0.02) * this.dpr };
  }

  destroy(): void {
    this.particles = [];
  }
}
